import { ActionsDropdown, ModalAlert } from '@molecules';
import { PageHeader } from '@organisms';
import { PageListTemplate } from '@templates';
import { Button, Tag } from 'antd';
import { format, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { TenantListProps } from '../../types/tenantList.types';
import { TenantListFilter } from './components/TenantListFilter';
import { StatusViewByStatus } from './constants/StatusViewByStatus.const';
import { useTenantList } from './hooks/useTenantList';

export const TenantList = () => {
  const {
    tenantList,
    pageSize,
    pageSort,
    currentPage,
    isLoading,
    tenantToToggleStatus,
    handleChangePagination,
    handleChangeFilter,
    handleChangeSort,
    handleBeforeDisableTenant,
    handleToggleTenantStatus,
    handleCancelToggleTenantStatus,
  } = useTenantList();
  const navigate = useNavigate();

  const headerColumns: TenantListProps['columns'] = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      sorter: true,
      sortOrder: pageSort?.field === 'id' ? pageSort.order : null,
    },
    {
      title: 'Tenant',
      dataIndex: 'fantasyName',
      key: 'fantasyName',
      sorter: true,
      sortOrder: pageSort?.field === 'fantasyName' ? pageSort.order : null,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: TenantListProps['data']['status']) => {
        const { Icon, color, text } = StatusViewByStatus[status];
        return (
          <Tag icon={Icon} color={color}>
            {text}
          </Tag>
        );
      },
      sorter: true,
      sortOrder: pageSort?.field === 'status' ? pageSort.order : null,
    },
    {
      title: 'CNPJ',
      dataIndex: 'document',
      key: 'document',
      render: (document: TenantListProps['data']['document']) => {
        return document.replace(
          /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
          '$1.$2.$3/$4-$5'
        );
      },
      sorter: true,
      sortOrder: pageSort?.field === 'document' ? pageSort.order : null,
    },
    {
      title: 'TenantAlias',
      dataIndex: 'tenantAliasId',
      key: 'tenantAliasId',
      sorter: true,
      sortOrder: pageSort?.field === 'tenantAliasId' ? pageSort.order : null,
    },
    {
      title: 'Criado em',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: '120px',
      render: (createdAt: TenantListProps['data']['createdAt']) => {
        return format(parseISO(createdAt), 'dd/MM/yyyy');
      },
      sorter: true,
      sortOrder: pageSort?.field === 'createdAt' ? pageSort.order : null,
    },
    {
      title: 'Ações',
      key: 'action',
      dataIndex: 'index',
      width: '2%',
      render: (_text, record) => (
        <ActionsDropdown
          onClickEditOption={() => {
            navigate(`/tenants/${record.id}`);
          }}
          customOptions={[
            {
              label: StatusViewByStatus[record.status].toggleAction,
              icon: <></>,
              onClick: () => {
                handleBeforeDisableTenant(record);
              },
            },
          ]}
        />
      ),
    },
  ];

  const toggleStatusModalInfo = useMemo(() => {
    if (!tenantToToggleStatus) return undefined;
    return {
      titleLabel: `${
        StatusViewByStatus[tenantToToggleStatus.status].toggleAction
      } Tenant ${tenantToToggleStatus.fantasyName}`,
      messageLabel: `Você tem certeza que deseja ${
        StatusViewByStatus[tenantToToggleStatus.status].toggleAction
      } o tenant ${tenantToToggleStatus.fantasyName} (${
        tenantToToggleStatus.tenantAliasId
      })?`,
    };
  }, [tenantToToggleStatus]);

  return (
    <>
      <PageHeader
        backToPath='dashboard'
        title={'Gestão de Tenant'}
        subTitle=''
        extra={
          <Button
            key={'filter-header-button-add-assets'}
            type='primary'
            onClick={() => navigate('/tenants/create')}
          >
            Criar tenant
          </Button>
        }
      />
      <PageListTemplate
        filterComponent={<TenantListFilter onFilter={handleChangeFilter} />}
        columns={headerColumns}
        data={tenantList}
        loading={isLoading}
        onChange={handleChangePagination}
        onChangeSort={handleChangeSort}
        pageSize={pageSize}
        currentPage={currentPage}
      />
      <ModalAlert
        showModal={!!tenantToToggleStatus}
        confirmButtonAction={handleToggleTenantStatus}
        denyButtonAction={handleCancelToggleTenantStatus}
        titleLabel={toggleStatusModalInfo?.titleLabel ?? ''}
        messageLabel={toggleStatusModalInfo?.messageLabel ?? ''}
      />
    </>
  );
};
